import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Button,
  Chip,
  Box,
  Alert,
  Snackbar,
  Avatar,
  Fade,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Description,
  GetApp,
  Download,
  Business,
  Home,
  Work,
  Build,
  CardGiftcard,
  TrendingUp,
  Star,
  AccessTime,
  CheckCircle,
  Launch,
  AutoFixHigh,
  Shield,
  Speed,
} from '@mui/icons-material';
import GenericContractForm from '../components/GenericContractForm';
import GiftContractForm from '../components/GiftContractForm';
import AppleStylePurchaseContractForm from '../components/AppleStylePurchaseContractFormNew';
import { purchaseContractConfig, rentalContractConfig, employmentContractConfig, workContractConfig, giftContractConfig } from '../configs/contracts';
import ContractPreview from '../components/ContractPreview';
import { api } from '../services/api';
import { DocumentManager } from '../utils/DocumentManager';
import Sidebar from '../components/Sidebar';
import DocumentHistory from '../components/DocumentHistory';
import { getErrorMessage } from '../utils/errorUtils';
import FeatureLockModal from '../components/FeatureLockModal';
import { PAY_PER_USE_PRICING, CREDIT_VALUE_CZK } from '../config/pricing';

const DocumentsPage: React.FC = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [currentFormType, setCurrentFormType] = useState<'purchase' | 'rental' | 'employment' | 'work' | 'gift'>('purchase');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [contractData, setContractData] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const { collapsed } = useSidebar();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const documentsPricing = PAY_PER_USE_PRICING.find(item => item.key === 'document-generation');
  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const documentTemplates = [
    {
      id: 1,
      name: 'Kupní smlouva',
      description: 'Profesionální šablona pro kupní smlouvu movitých věcí s automatickou kontrolou',
      category: 'Obchodní právo',
      complexity: 'Jednoduchá',
      fields: ['Prodávající', 'Kupující', 'Předmět koupě', 'Cena'],
      icon: <Business fontSize="large" />,
      popular: true,
      features: ['AI doporučení', 'Auto-vyplnění', 'PDF export', 'Právní kontrola']
    },
    {
      id: 2,
      name: 'Nájemní smlouva',
      description: 'Komplexní šablona pro nájem bytu nebo domu s ochranou obou stran',
      category: 'Občanské právo',
      complexity: 'Střední',
      fields: ['Pronajímatel', 'Nájemník', 'Objekt nájmu', 'Nájemné'],
      icon: <Home fontSize="large" />,
      popular: true,
      features: ['Ochrana nájemníka', 'Kauce kalkulace', 'Termíny upozornění', 'Energetická náročnost']
    },
    {
      id: 3,
      name: 'Pracovní smlouva',
      description: 'Moderní pracovní smlouva podle aktuálního zákoníku práce s benefity',
      category: 'Pracovní právo',
      complexity: 'Střední',
      fields: ['Zaměstnavatel', 'Zaměstnanec', 'Pozice', 'Mzda'],
      icon: <Work fontSize="large" />,
      popular: false,
      features: ['GDPR compliance', 'Homeoffice klauzule', 'Benefity', 'Konkurenční doložka']
    },
    {
      id: 4,
      name: 'Smlouva o dílo',
      description: 'Profesionální smlouva pro zadání díla s detailní specifikací a zárukami',
      category: 'Obchodní právo',
      complexity: 'Střední',
      fields: ['Zhotovitel', 'Objednavatel', 'Předmět díla', 'Cena'],
      icon: <Build fontSize="large" />,
      popular: true,
      features: ['Záruční podmínky', 'Platební kalendář', 'Smluvní pokuty', 'Pojištění díla']
    },
    {
      id: 5,
      name: 'Darovací smlouva',
      description: 'Kompletní šablona pro darování movitých i nemovitých věcí s daňovými aspekty',
      category: 'Občanské právo',
      complexity: 'Jednoduchá',
      fields: ['Dárce', 'Obdarovaný', 'Předmět darovani', 'Hodnota'],
      icon: <CardGiftcard fontSize="large" />,
      popular: false,
      features: ['Nemovitosti support', 'Daňové povinnosti', 'Notářské ověření', 'Katastrální údaje']
    },
  ];

  // Připravené skupiny pro rozložení 3 + 2 (bez posouvání)
  const firstRow = documentTemplates.slice(0, 3);
  const secondRow = documentTemplates.slice(3);

  // Render jedné karty šablony (zachován původní vzhled)
  const renderTemplateCard = (template: any) => (
    <Box
      key={template.id}
      sx={{
        width: {
          xs: '100%',
          sm: 'calc((100% - 24px)/2)', // 2 sloupce na sm
          md: 'calc((100% - 64px)/3)', // 3 sloupce na md (gap ~ 32px)
        },
        backgroundColor: '#FFFFFF',
        borderRadius: '24px',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
        padding: { xs: '24px 20px 20px', sm: '36px 30px 30px', md: '48px 40px 40px' },
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        position: 'relative',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        {template.popular && (
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <Chip
              icon={<Star sx={{ fontSize: 16, color: '#e72f0d' }} />}
              label="Populární"
              size="small"
              sx={{
                background: 'rgba(231, 47, 13, 0.1)',
                color: '#e72f0d',
                fontWeight: 700,
                fontSize: '0.75rem',
                border: '1px solid rgba(231, 47, 13, 0.3)',
                boxShadow: '0 2px 8px rgba(231, 47, 13, 0.15)'
              }}
            />
          </Box>
        )}

        <Avatar
          className="template-icon"
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 3,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.4)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.03), inset 0 2px 4px rgba(0, 0, 0, 0.02)',
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            '& svg': { color: '#424242', fontSize: 40 }
          }}
        >
          {template.icon}
        </Avatar>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#212121', textAlign: 'center' }}>
          {template.name}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.7, color: '#424242', textAlign: 'center' }}>
          {template.description}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Chip
            label={template.category}
            size="small"
            sx={{
              background: 'rgba(31, 31, 31, 0.08)',
              color: '#1F1F1F',
              fontWeight: 600,
              border: '1px solid rgba(31, 31, 31, 0.15)',
              boxShadow: 'none'
            }}
          />
          <Chip
            label={template.complexity}
            size="small"
            sx={{
              background: template.complexity === 'Jednoduchá'
                ? 'rgba(76, 175, 80, 0.1)'
                : template.complexity === 'Střední'
                ? 'rgba(231, 47, 13, 0.08)'
                : 'rgba(231, 47, 13, 0.15)',
              color: template.complexity === 'Jednoduchá' ? '#2e7d32' : '#e72f0d',
              fontWeight: 600,
              border: template.complexity === 'Jednoduchá'
                ? '1px solid rgba(76, 175, 80, 0.3)'
                : '1px solid rgba(231, 47, 13, 0.3)',
              boxShadow: 'none'
            }}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#424242', textAlign: 'center' }}>
            Klíčové funkce:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
            {template.features.map((feature: string, idx: number) => (
              <Chip
                key={idx}
                label={feature}
                size="small"
                sx={{
                  background: 'rgba(0, 0, 0, 0.04)',
                  color: '#424242',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  '&:hover': { background: 'rgba(0, 0, 0, 0.06)', borderColor: 'rgba(0, 0, 0, 0.12)' },
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box>
        <Button
          variant="outlined"
          fullWidth
          size="large"
          endIcon={<Launch />}
          onClick={() => handleGenerateDocument(template.id)}
          sx={{
            width: '100%',
            padding: '14px 16px',
            backgroundColor: '#1F1F1F',
            color: '#FFFFFF',
            borderRadius: '999px',
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            border: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#e72f0d',
              '& .MuiButton-endIcon': { transform: 'translateX(4px)' }
            },
            '& .MuiButton-endIcon': { transition: 'transform 0.4s ease-out' }
          }}
        >
          Vytvořit dokument
        </Button>
      </Box>
    </Box>
  );

  const handleGenerateDocument = (templateId: number) => {
    // If unauthenticated, redirect to dashboard with registration prompt (register mode)
    if (!isAuthenticated) {
      const intended = '/dashboard?open=documents';
      try { sessionStorage.setItem('intendedPath', intended); } catch {}
      navigate(`/dashboard?requireRegistration=1&authMode=login&intendedPath=${encodeURIComponent(intended)}`);
      return;
    }
    switch (templateId) {
      case 1: // Kupní smlouva
        setCurrentFormType('purchase');
        setFormOpen(true);
        break;
      case 2: // Nájemní smlouva
        setCurrentFormType('rental');
        setFormOpen(true);
        break;
      case 3: // Pracovní smlouva
        setCurrentFormType('employment');
        setFormOpen(true);
        break;
      case 4: // Smlouva o dílo
        setCurrentFormType('work');
        setFormOpen(true);
        break;
      case 5: // Darovací smlouva
        setCurrentFormType('gift');
        setFormOpen(true);
        break;
      default:
        alert(`Generování dokumentu ${templateId} - funkcionalita bude implementována`);
    }
  };

  const handlePreview = async (formData: any) => {
    try {
      // Determine contract type from currentFormType
      const contractType = currentFormType;
      const apiEndpoint = DocumentManager.getApiEndpoint(contractType);

      // Call backend preview API
      const response = await api.post(`/documents/test/preview/${apiEndpoint}`, formData);

      if (response?.data) {
        // Transform the preview data with form data included
        const transformedData = {
          text_content: response.data.preview_text || '',
          filename: `${DocumentManager.getDisplayName(contractType).toLowerCase().replace(' ', '_')}_${Date.now()}.pdf`,
          download_url: '/api/v1/documents/generate',
          generated_at: new Date().toISOString(),
          contract_type: contractType,
          form_data: formData // Keep original form data for generation
        };

        setContractData(transformedData);
        setPreviewOpen(true);
      }
    } catch (error: any) {
      console.error('Preview error:', error);
      let message = getErrorMessage(error, 'Neznámá chyba');
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
      }
      setSuccessMessage(`Chyba při generování náhledu: ${message}`);
    }
  };

  const handleDownload = async (modifiedContent?: string) => {
    await DocumentManager.downloadDocument(
      contractData,
      setDownloading,
      setSuccessMessage,
      modifiedContent
    );
  };

  const handleDownloadDocx = async (modifiedContent?: string) => {
    await DocumentManager.downloadDocumentDocx(
      contractData,
      setDownloading,
      setSuccessMessage,
      modifiedContent
    );
  };

  // (Carousel code removed – statické rozložení 3 + 2)

  // Dočasně uzamčeno: Generování dokumentů
  const FEATURE_LOCKED = false;
  if (FEATURE_LOCKED) {
    return (
      <FeatureLockModal
        serviceName="Generování dokumentů"
        description="Tato sekce je zatím v přípravě. Mezitím prosím vyzkoušejte jiné funkce, které jsou již dostupné."
        suggestions={[
          { label: 'Právní konzultace', to: '/consultation', variant: 'contained' },
          { label: 'Analýza smluv', to: '/contract-analysis', variant: 'outlined' }
        ]}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', width: '100%' }}>
      <Sidebar />

      <Box sx={{
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        transition: 'margin-left 0.3s ease',
        overflow: 'auto',
        maxWidth: '100%'
      }}>
        <Box sx={{
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 6 },
          maxWidth: '100%',
          overflow: 'hidden'
        }}>

        {documentsPricing && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 5,
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: '18px',
            px: { xs: 2.5, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            boxShadow: '0 12px 32px rgba(0,0,0,0.08)'
          }}>
            <Chip
              label={`${formatCurrency(documentsPricing.priceCZK)} Kč`}
              sx={{
                backgroundColor: '#1F1F1F',
                color: '#FFFFFF',
                fontWeight: 600
              }}
            />
            <Chip
              label={`${formatCurrency(documentsPricing.credits)} kreditů`}
              sx={{
                backgroundColor: 'rgba(231,47,13,0.12)',
                color: '#e72f0d',
                fontWeight: 600
              }}
            />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#424242', fontWeight: 500 }}>
              1 kredit = {creditValue} Kč. Platba se odečte až po stažení finálního dokumentu.
            </Typography>
          </Box>
        )}

        {/* How it works section */}
        <Fade in timeout={1600}>
          <Box sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            boxShadow: '0px 2px 2px rgba(18, 14, 13, 0.15)',
            mb: 8
          }}>
            <Box sx={{
              textAlign: 'center',
              p: 6,
              position: 'relative'
            }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#212121',
                }}
              >
                Jak generování funguje?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#424242',
                  fontWeight: 400,
                  mb: 4
                }}
              >
                Jednoduchý proces v 3 krocích
              </Typography>
            </Box>

            <Box sx={{ p: 6, pt: 0 }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 6,
                textAlign: 'center'
              }}>
                <Box sx={{ flex: 1 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.03)',
                    '& svg': {
                      color: '#424242'
                    }
                  }}>
                    <Description />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'var(--liquid-gray-900)' }}>
                    1. Vyber šablonu
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#424242' }}>
                    Zvol si ze široké nabídky právních dokumentů připravených našimi experty
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.03)',
                    '& svg': {
                      color: '#424242'
                    }
                  }}>
                    <AutoFixHigh />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'var(--liquid-gray-900)' }}>
                    2. Vyplň údaje
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#424242' }}>
                    Průvodce tě provede vyplňováním s inteligentními návrhy a kontrolami
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.03)',
                    '& svg': {
                      color: '#424242'
                    }
                  }}>
                    <Download />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'var(--liquid-gray-900)' }}>
                    3. Stáhni dokument
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#424242' }}>
                    Získej profesionální PDF dokument připravený k podpisu a použití
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* Jednoduchý návod – Generování dokumentů (v aplikaci) */}
        {!formOpen && (
          <Box sx={{
            mt: { xs: 1, md: 2 },
            p: { xs: 2.5, sm: 3 },
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            maxWidth: 900,
            mx: 'auto',
            mb: { xs: 4, sm: 5 }
          }}>
            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
              Jak postupovat – Generování dokumentů
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>1) Zvolte šablonu</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Klikněte na požadovaný dokument (např. Kupní/Nájemní/Pracovní smlouva).</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>2) Vyplňte formulář</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Jednoduché otázky – jméno, částky, termíny. Náhled uvidíte hned.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>3) Stáhněte PDF</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Hotový dokument stáhnete jako PDF a máte hotovo.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>4) Historie v Dashboardu</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Poslední dokumenty uvidíte v Dashboardu – kdykoliv k dispozici.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Šablony dokumentů – rozložení 3 + 2 (bez posouvání) */}
        <Box sx={{ mb: 8, width: '100%', px: { xs: 0, sm: 2, md: 4 } }}>
          {/* První řada: 3 karty */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: { xs: 'center', md: 'space-between' },
              gap: { xs: 2, sm: 3, md: 4 }
            }}
          >
            {firstRow.map(renderTemplateCard)}
          </Box>

          {/* Druhá řada: 2 karty uprostřed */}
          <Box
            sx={{
              mt: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: 'center',
              gap: { xs: 2, sm: 3, md: 4 }
            }}
          >
            {secondRow.map(renderTemplateCard)}
          </Box>
        
          {/* Document history section */}
          <Box sx={{ mt: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Nedávno vygenerované dokumenty
            </Typography>
            <DocumentHistory />
          </Box>
        </Box>
        </Box>
      </Box>

      {/* Form Dialogs - Using Old Working AppleStylePurchaseContractForm */}
      {formOpen && currentFormType === 'purchase' && (
        <AppleStylePurchaseContractForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditMode(false);
          }}
          onSuccess={async (payload: any) => {
            try {
              // Pokud AppleStyle formulář vrátil hotový výsledek (pdfResponse.data + form_data)
              if (payload && payload.filename) {
                const updated = {
                  ...payload,
                  contract_type: 'purchase'
                };
                setContractData(updated);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('purchase')} byla úspěšně vygenerována! Soubor: ${payload.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updated }));
                return;
              }

              // Fallback: payload je pouze formData → zavolat API zde
              const formData = payload;
              const apiEndpoint = DocumentManager.getApiEndpoint('purchase');
              const prefix = isAuthenticated ? '/documents/generate' : '/documents/test/generate';
              const response = await api.post(`${prefix}/${apiEndpoint}`, formData);

              if (response?.data) {
                const updatedContractData = {
                  ...response.data,
                  contract_type: 'purchase',
                  form_data: formData
                };
                setContractData(updatedContractData);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('purchase')} byla úspěšně vygenerována! Soubor: ${response.data.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updatedContractData }));
              }
            } catch (error: any) {
              // Surface server validation details for easier debugging (422 Unprocessable Entity)
              console.error('Generation error [purchase]:', error?.response?.data || error);
              let message = getErrorMessage(error, 'Neznámá chyba');
              const detail = error?.response?.data?.detail;
              if (Array.isArray(detail)) {
                message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
              }
              setSuccessMessage(`Chyba při generování dokumentu: ${message}`);
            }
          }}
          onPreview={handlePreview}
          editMode={editMode}
          existingData={editMode ? contractData?.form_data : null}
        />
      )}

      {formOpen && currentFormType === 'rental' && (
        <GenericContractForm
          config={rentalContractConfig}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditMode(false);
          }}
          onSuccess={async (formData: any) => {
            try {
              const apiEndpoint = DocumentManager.getApiEndpoint('rental');
              const prefix = isAuthenticated ? '/documents/generate' : '/documents/test/generate';
              const response = await api.post(`${prefix}/${apiEndpoint}`, formData);

              if (response?.data) {
                const updatedContractData = {
                  ...response.data,
                  contract_type: 'rental',
                  form_data: formData
                };

                setContractData(updatedContractData);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('rental')} byla úspěšně vygenerována! Soubor: ${response.data.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updatedContractData }));
              }
            } catch (error: any) {
              console.error('Generation error [rental]:', error?.response?.data || error);
              let message = getErrorMessage(error, 'Neznámá chyba');
              const detail = error?.response?.data?.detail;
              if (Array.isArray(detail)) {
                message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
              }
              setSuccessMessage(`Chyba při generování dokumentu: ${message}`);
            }
          }}
          onPreview={handlePreview}
          editMode={editMode}
          existingData={editMode ? contractData?.form_data : null}
          isDialog={true}
        />
      )}

      {formOpen && currentFormType === 'employment' && (
        <GenericContractForm
          config={employmentContractConfig}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditMode(false);
          }}
          onSuccess={async (formData: any) => {
            try {
              const apiEndpoint = DocumentManager.getApiEndpoint('employment');
              const prefix = isAuthenticated ? '/documents/generate' : '/documents/test/generate';
              const response = await api.post(`${prefix}/${apiEndpoint}`, formData);

              if (response?.data) {
                const updatedContractData = {
                  ...response.data,
                  contract_type: 'employment',
                  form_data: formData
                };

                setContractData(updatedContractData);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('employment')} byla úspěšně vygenerována! Soubor: ${response.data.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updatedContractData }));
              }
            } catch (error: any) {
              console.error('Generation error [employment]:', error?.response?.data || error);
              let message = getErrorMessage(error, 'Neznámá chyba');
              const detail = error?.response?.data?.detail;
              if (Array.isArray(detail)) {
                message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
              }
              setSuccessMessage(`Chyba při generování dokumentu: ${message}`);
            }
          }}
          onPreview={handlePreview}
          editMode={editMode}
          existingData={editMode ? contractData?.form_data : null}
          isDialog={true}
        />
      )}

      {formOpen && currentFormType === 'work' && (
        <GenericContractForm
          config={workContractConfig}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditMode(false);
          }}
          onSuccess={async (formData: any) => {
            try {
              const apiEndpoint = DocumentManager.getApiEndpoint('work');
              const prefix = isAuthenticated ? '/documents/generate' : '/documents/test/generate';

              let response;
              try {
                response = await api.post(`${prefix}/${apiEndpoint}`, formData);
              } catch (err) {
                // Fallback for missing authenticated endpoint
                response = await api.post(`/documents/test/generate/${apiEndpoint}`, formData);
              }

              if (response?.data) {
                const updatedContractData = {
                  ...response.data,
                  contract_type: 'work',
                  form_data: formData
                };

                setContractData(updatedContractData);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('work')} byla úspěšně vygenerována! Soubor: ${response.data.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updatedContractData }));
              }
            } catch (error: any) {
              console.error('Generation error [work]:', error?.response?.data || error);
              let message = getErrorMessage(error, 'Neznámá chyba');
              const detail = error?.response?.data?.detail;
              if (Array.isArray(detail)) {
                message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
              }
              setSuccessMessage(`Chyba při generování dokumentu: ${message}`);
            }
          }}
          onPreview={handlePreview}
          editMode={editMode}
          existingData={editMode ? contractData?.form_data : null}
          isDialog={true}
        />
      )}

      {formOpen && currentFormType === 'gift' && (
        <GiftContractForm
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditMode(false);
          }}
          onSuccess={async (formData: any) => {
            try {
              // Sanitizace payloadu pro backend (pydantic int pole nesmí být prázdné stringy)
              const sanitizeGift = (data: any) => {
                const toNumber = (v: any) => (v === '' || v === null || v === undefined ? undefined : Number(v));
                const out: any = { ...data };
                if ('money_amount' in out) out.money_amount = toNumber(out.money_amount);
                if ('gift_value' in out) out.gift_value = toNumber(out.gift_value);
                Object.keys(out).forEach((k) => { if (out[k] === undefined) delete out[k]; });
                return out;
              };
              const payload = sanitizeGift(formData);
              const apiEndpoint = DocumentManager.getApiEndpoint('gift');
              const prefix = isAuthenticated ? '/documents/generate' : '/documents/test/generate';
              let response;
              try {
                response = await api.post(`${prefix}/${apiEndpoint}`, payload);
              } catch (err) {
                response = await api.post(`/documents/test/generate/${apiEndpoint}`, payload);
              }
              if (response?.data) {
                const updated = { ...response.data, contract_type: 'gift', form_data: payload };
                setContractData(updated);
                setFormOpen(false);
                setEditMode(false);
                setPreviewOpen(true);
                setSuccessMessage(`${DocumentManager.getDisplayName('gift')} byla úspěšně vygenerována! Soubor: ${response.data.filename}`);
                window.dispatchEvent(new CustomEvent('document-generated', { detail: updated }));
              }
            } catch (error: any) {
              console.error('Generation error [gift]:', error?.response?.data || error);
              let message = getErrorMessage(error, 'Neznámá chyba');
              const detail = error?.response?.data?.detail;
              if (Array.isArray(detail)) {
                message = detail.map((d: any) => d?.msg || '').filter(Boolean).join('; ') || message;
              }
              setSuccessMessage(`Chyba při generování dokumentu: ${message}`);
            }
          }}
          onPreview={handlePreview}
          editMode={editMode}
          existingData={editMode ? contractData?.form_data : null}
        />
      )}

      <ContractPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        contractData={contractData}
        onDownload={handleDownload}
        onDownloadDocx={handleDownloadDocx}
        onEdit={() => {
          // Vrátit zpět na rozdělený formulář v edit módu
          setEditMode(true);
          setPreviewOpen(false);
          setFormOpen(true);
        }}
        downloading={downloading}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage('')} 
          severity="success"
          role="alert"
          aria-live="polite"
          sx={{ 
            width: '100%',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#424242'
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsPage;
